/* eslint-disable camelcase */
// OrderCard.tsx

import {
  Card,
  Flex,
  Stack,
  Button,
  Text,
  VStack,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Box,
  HStack,
  SimpleGrid,
  Tfoot,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaStore,
  FaClock,
  FaPhone,
  FaInfoCircle,
  FaShoppingBag,
  FaUtensils,
  FaCalendar,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaDirections,
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { OrderState } from './OrderStateSelect';
import { Basket_Dish, Order } from 'src/utils/__generated__/graphql';

interface OrderCardProps {
  order: Order;
  onCancel?: (orderId: string) => void;
  onAccept?: (orderId: string, orderState: OrderState) => void;
  onStartAssigned?: (orderId: string) => void;
  refetch?: () => void;
  userRole: string | undefined;
}

const formatTimeAgo = (dateString: string) => {
  const now = new Date();
  const past = new Date(dateString);
  const diffInMinutes = Math.floor((now.getTime() - past.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes === 1) return '1 minute ago';
  if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours === 1) return '1 hour ago';
  if (diffInHours < 24) return `${diffInHours} hours ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return '1 day ago';
  return `${diffInDays} days ago`;
};

const getStatusConfig = (status: OrderState) => {
  const configs = {
    [OrderState.PENDING]: {
      color: 'yellow',
      icon: FaClock,
      priority: 'high',
    },
    [OrderState.ACCEPTED]: {
      color: 'green',
      icon: FaCheckCircle,
      priority: 'medium',
    },
    [OrderState.COOKING]: {
      color: 'blue',
      icon: FaUtensils,
      priority: 'medium',
    },
    [OrderState.ASSIGNED]: {
      color: 'purple',
      icon: FaShoppingBag,
      priority: 'low',
    },
    [OrderState.SHIPPING]: {
      color: 'orange',
      icon: FaShoppingBag,
      priority: 'low',
    },
    [OrderState.DELIVERED]: {
      color: 'green',
      icon: FaCheckCircle,
      priority: 'low',
    },
    [OrderState.CANCELED]: {
      color: 'red',
      icon: FaExclamationTriangle,
      priority: 'low',
    },
    [OrderState.COMPLETED]: {
      color: 'green',
      icon: FaCheckCircle,
      priority: 'low',
    },
  };
  return configs[status] || configs[OrderState.PENDING];
};

const ActionButtons = ({
  orderState,
  orderId,
  onCancel,
  onAccept,
  onStartAssigned,
  order,
}: {
  orderState: OrderState;
  orderId: string;
  onCancel?: (orderId: string) => void;
  onAccept?: (orderId: string, orderState: OrderState) => void;
  onStartAssigned?: (orderId: string) => void;
  order: Order;
}) => {
  if (orderState === OrderState.PENDING) {
    return (
      <Stack direction={{ base: 'column' }} spacing={3} w="full">
        <Button
          onClick={() => onAccept && onAccept(orderId, OrderState.COOKING)}
          flex={1}
          colorScheme="green"
          variant="outline"
          bg="green.600"
          color="white"
          size="lg"
          minW="200px"
          leftIcon={<Icon as={FaCheckCircle as any} />}
        >
          Accept
        </Button>
        <Button
          onClick={() => onCancel && onCancel(order?.id)}
          flex={1}
          colorScheme="red"
          variant="outline"
          size="lg"
          leftIcon={<Icon as={FaTimesCircle as any} />}
        >
          Cancel
        </Button>
      </Stack>
    );
  }

  if (orderState === OrderState.ACCEPTED) {
    return (
      <Button
        onClick={() => onAccept && onAccept(orderId, OrderState.COOKING)}
        w="full"
        colorScheme="blue"
        size="lg"
        leftIcon={<Icon as={FaUtensils as any} />}
      >
        Start Cooking
      </Button>
    );
  }

  if (orderState === OrderState.COOKING) {
    return (
      <Button
        onClick={() => onStartAssigned && onStartAssigned(orderId)}
        w="full"
        colorScheme="purple"
        size="lg"
        leftIcon={<Icon as={FaShoppingBag as any} />}
      >
        Ready for Pickup
      </Button>
    );
  }

  if (orderState === OrderState.ASSIGNED) {
    return (
      <Box
        w="full"
        p={3}
        bg="orange.50"
        borderWidth="1px"
        borderColor="orange.200"
        borderRadius="md"
        textAlign="center"
      >
        <HStack justify="center" spacing={2} color="orange.700">
          <Icon as={FaShoppingBag as any} />
          <Text fontSize="sm" fontWeight="medium">
            Delivery Partner En Route
          </Text>
        </HStack>
      </Box>
    );
  }

  return null;
};

export const OrderCard: React.FC<OrderCardProps> = ({ order, onCancel, onAccept, onStartAssigned, userRole }) => {
  const orderedDate = formatTimeAgo(order?.created_at);
  const orderIdLast6Digits = order?.id.slice(-6).toUpperCase();
  const statusConfig = getStatusConfig(order.status as OrderState);
  const StatusIcon = statusConfig.icon;
  const navigate = useNavigate();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const tableRowBg = useColorModeValue('white', 'gray.800');
  const tableRowHoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card
      borderRadius="lg"
      shadow="md"
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      _hover={{ shadow: 'lg' }}
      transition="all 0.2s"
      overflow="hidden"
    >
      {/* Priority Indicator */}
      {statusConfig.priority === 'high' && (
        <Box
          h="2px"
          bgGradient="linear(to-r, red.500, orange.500)"
          position="absolute"
          top="0"
          left="0"
          right="0"
          zIndex="1"
        />
      )}

      {/* Header */}
      <Box p={6} borderBottom="1px" borderColor={borderColor}>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', lg: 'center' }}
          gap={4}
        >
          {/* Order Info */}
          <Flex align="center" gap={4}>
            <Box p={4} borderRadius="md" bg={`${statusConfig.color}.100`} color={`${statusConfig.color}.600`}>
              <Icon as={StatusIcon as any} boxSize={7} />
            </Box>
            <Box>
              <Flex align="center" gap={3} mb={1}>
                <Text fontSize="2xl" fontWeight="bold" color={`${statusConfig.color}.600`}>
                  #{orderIdLast6Digits}
                </Text>
                <Badge
                  colorScheme={statusConfig.color}
                  variant="subtle"
                  fontSize="xs"
                  fontWeight="medium"
                  borderRadius="full"
                  px={2}
                  py={1}
                >
                  {order.status}
                </Badge>
              </Flex>
              <HStack spacing={4} fontSize="sm" color={mutedColor}>
                <HStack spacing={1}>
                  <Icon as={FaCalendar as any} boxSize={3} />
                  <Text fontSize="md" fontWeight="medium" color={mutedColor}>
                    {orderedDate}
                  </Text>
                </HStack>
              </HStack>
            </Box>
          </Flex>

          {/* Action Buttons */}
          {order?.status !== OrderState.SHIPPING &&
            order?.status !== OrderState.COMPLETED &&
            order?.status !== OrderState.DELIVERED &&
            order?.status !== OrderState.CANCELED && (
              <Box w={{ base: 'full', lg: 'auto' }} minW={{ lg: '200px' }}>
                <ActionButtons
                  orderState={order?.status as OrderState}
                  orderId={order?.id}
                  onCancel={onCancel}
                  onAccept={onAccept}
                  onStartAssigned={onStartAssigned}
                  order={order}
                />
              </Box>
            )}
        </Flex>
      </Box>

      {/* Content */}
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          {/* Key Information */}
          <SimpleGrid columns={{ base: 2 }} spacing={4}>
            {/* Customer Details */}
            <Box>
              <VStack align="start" spacing={1}>
                <Text fontSize="md" fontWeight="semibold" color={textColor}>
                  {order?.receiverName}
                </Text>

                <HStack spacing={4} fontSize="sm" color={mutedColor}>
                  <HStack spacing={1} fontSize="sm" color={mutedColor}>
                    <Icon as={FaPhone as any} boxSize={3} />
                    <Text>{order?.phoneNumber}</Text>
                  </HStack>

                  <HStack spacing={1} fontSize="sm" color={mutedColor}>
                    <Icon as={FaDirections as any} boxSize={3} />
                    <Text>{order?.direction}</Text>
                  </HStack>
                </HStack>

                <HStack spacing={1} fontSize="sm" color={mutedColor}>
                  <Icon as={FaMapMarkerAlt as any} boxSize={3} />
                  <Text>{order?.address}</Text>
                </HStack>
              </VStack>
            </Box>

            {/* Total Amount */}
            <Box flex={1} justifyContent="flex-end" alignItems="flex-end" w="full">
              <Text fontSize="2xl" fontWeight="bold" color="green.600" ml="auto" mr="auto" align="end">
                Rs. {order?.subTotal.toFixed(0)}
              </Text>
              <Text fontSize="lg" fontWeight="semibold" color={mutedColor} align="end">
                {order.basket.basket_dishes.reduce((acc, dish) => acc + dish.quantity, 0)} Items
              </Text>
            </Box>
          </SimpleGrid>

          {/* Restaurant Details (if admin) */}
          {userRole === 'admin' && (
            <Link to={`/admin/edit-restaurant/${order?.basket?.restaurant?.id}`}>
              <Box _hover={{ opacity: 0.8 }} transition="opacity 0.2s">
                <HStack spacing={2} mb={2}>
                  <Icon as={FaStore as any} color="purple.500" boxSize={4} />
                  <Text fontSize="sm" fontWeight="medium" color={mutedColor} textTransform="uppercase">
                    Restaurant
                  </Text>
                </HStack>
                <Text fontSize="md" fontWeight="semibold" color={textColor}>
                  {order?.basket?.restaurant?.name}
                </Text>
              </Box>
            </Link>
          )}

          {/* Special Instructions */}
          {order?.note && (
            <Box p={4} bg="yellow.50" borderWidth="1px" borderColor="yellow.200" borderRadius="md">
              <HStack spacing={3}>
                <Icon as={FaInfoCircle as any} color="yellow.600" boxSize={4} />
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="yellow.800" mb={1}>
                    Special Instructions
                  </Text>
                  <Text fontSize="sm" color="yellow.700">
                    {order.note}
                  </Text>
                </Box>
              </HStack>
            </Box>
          )}

          {/* Order Items */}
          <Box>
            <HStack spacing={2} mb={4}>
              <Icon as={FaShoppingBag as any} color="indigo.500" boxSize={4} />
              <Text fontSize="md" fontWeight="semibold" color={mutedColor}>
                Order Items ({order.basket.basket_dishes.reduce((acc, dish) => acc + dish.quantity, 0)})
              </Text>
            </HStack>

            <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
              <Box overflowX="auto">
                <Table variant="simple" size="sm" onClick={() => navigate(`/order/${order?.id}`)}>
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs" fontWeight="medium" color={mutedColor} textTransform="uppercase" py={3} px={4}>
                        Item Details
                      </Th>
                      <Th
                        fontSize="xs"
                        fontWeight="medium"
                        color={mutedColor}
                        textTransform="uppercase"
                        py={3}
                        px={4}
                        textAlign="center"
                      >
                        Quantity
                      </Th>
                      <Th
                        fontSize="xs"
                        fontWeight="medium"
                        color={mutedColor}
                        textTransform="uppercase"
                        py={3}
                        px={4}
                        textAlign="right"
                      >
                        Unit Price
                      </Th>
                      <Th
                        fontSize="xs"
                        fontWeight="medium"
                        color={mutedColor}
                        textTransform="uppercase"
                        py={3}
                        px={4}
                        textAlign="right"
                      >
                        Subtotal
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody bg={tableRowBg}>
                    {order.basket.basket_dishes.map((basketDish: Basket_Dish) => (
                      <Tr key={basketDish.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td py={3} px={4}>
                          <HStack spacing={3}>
                            {/* <Avatar
                              size="sm"
                              bg="indigo.500"
                              color="white"
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              {basketDish.dish?.name?.charAt(0) || '?'}
                            </Avatar> */}
                            <Box>
                              <Text fontSize="md" fontWeight="medium" color={textColor}>
                                {basketDish.dish?.name}
                              </Text>
                            </Box>
                          </HStack>
                        </Td>
                        <Td py={3} px={4} textAlign="center">
                          <Badge colorScheme="indigo" variant="subtle" fontSize="xs">
                            {basketDish.quantity}
                          </Badge>
                        </Td>
                        <Td py={3} px={4} textAlign="right">
                          <Text fontSize="md" color={textColor}>
                            ₹{Number(basketDish.dish?.price || 0).toFixed(2)}
                          </Text>
                        </Td>
                        <Td py={3} px={4} textAlign="right">
                          <Text fontSize="md" fontWeight="semibold" color={textColor}>
                            ₹{(basketDish.quantity * Number(basketDish.dish?.price || 0)).toFixed(2)}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                  <Tfoot bg={tableHeaderBg}>
                    <Tr>
                      <Td colSpan={3} py={3} px={4} textAlign="right" fontWeight="medium" color={mutedColor}>
                        Total:
                      </Td>
                      <Td py={3} px={4} textAlign="right">
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          ₹{order?.subTotal.toFixed(2)}
                        </Text>
                      </Td>
                    </Tr>
                  </Tfoot>
                </Table>
              </Box>
            </Box>
          </Box>
        </VStack>
      </Box>
    </Card>
  );
};
